import React, { useEffect, useState } from 'react';
import { Button, Card, Table, Pagination, Nav } from 'react-bootstrap';
import { database } from '../firebase';
import { ref, onValue, push, remove } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';

interface Count {
  id: string;
  timestamp: number;
}

const VeGaRides = () => {
  const [counts, setCounts] = useState<Count[]>([]);
  const { currentUser } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const location = useLocation();

  useEffect(() => {
    if (!currentUser) return;

    const countsRef = ref(database, `vega/${currentUser.uid}/counts`);
    const unsubscribe = onValue(countsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const countsArray = Object.entries(data).map(([id, timestamp]) => ({
        id,
        timestamp: timestamp as number
      }));
      setCounts(countsArray.sort((a, b) => b.timestamp - a.timestamp));
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleRemoveCount = async (countId: string) => {
    if (!currentUser) return;
    
    try {
      const countRef = ref(database, `vega/${currentUser.uid}/counts/${countId}`);
      await remove(countRef);
    } catch (error) {
      console.error('Error removing count:', error);
    }
  };

  // Pagination calculations
  const indexOfLastRide = currentPage * itemsPerPage;
  const indexOfFirstRide = indexOfLastRide - itemsPerPage;
  const currentRides = counts.slice(indexOfFirstRide, indexOfLastRide);
  const totalPages = Math.ceil(counts.length / itemsPerPage);

  // Generate pagination items
  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item 
        key={number} 
        active={number === currentPage}
        onClick={() => setCurrentPage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  if (!currentUser) {
    return (
      <Card>
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }}>VeGa Fahrten</Card.Title>
          <p>Please log in to view rides.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="container">
      <h2 className="mb-4">VeGa Fahrten</h2>
      
      {/* Navigation Tabs */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/vega" 
            active={location.pathname === '/dashboard/vega'}
          >
            Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/vega/rides" 
            active={location.pathname === '/dashboard/vega/rides'}
          >
            Fahrten
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/vega/settings" 
            active={location.pathname === '/dashboard/vega/settings'}
          >
            Konfiguration
          </Nav.Link>
        </Nav.Item>
      </Nav>
      
      <Card>
        <Card.Body>
          <Card.Title style={{ color: '#1780a6' }}>Fahrten</Card.Title>
          {counts.length > 0 ? (
            <>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Zeit</th>
                      <th>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRides.map(count => {
                      const date = new Date(count.timestamp * 1000);
                      return (
                        <tr key={count.id}>
                          <td>{date.toLocaleDateString()}</td>
                          <td>{date.toLocaleTimeString()}</td>
                          <td>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleRemoveCount(count.id)}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <div className="d-flex justify-content-center mt-3">
                <Pagination>
                  <Pagination.First 
                    onClick={() => setCurrentPage(1)} 
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev 
                    onClick={() => setCurrentPage(curr => Math.max(curr - 1, 1))}
                    disabled={currentPage === 1}
                  />
                  {paginationItems}
                  <Pagination.Next 
                    onClick={() => setCurrentPage(curr => Math.min(curr + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last 
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            </>
          ) : (
            <div className="text-muted text-center mt-3">
              Keine Fahrten vorhanden.
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default VeGaRides;
